import { Box, BoxProps, createStyles, CSSObject, MantineTheme, useMantineTheme } from "@mantine/core";
import { useMergedRef } from "@mantine/hooks";
import { PolymorphicComponentProps } from "@mantine/utils";
import React from "react";
import { IconV2 } from "../..";
import { RpiIconButton } from "../buttons/RpiIconButton";
import { Flex } from "../RpiFlex";

interface UseRpiDrawerInputStylesProps {
  isError?: boolean;
  isDisabled?: boolean;
  isFocused?: boolean;
  isEmpty?: boolean;
}

type RpiDrawerInputStyles = "root" | "label" | "input" | "error";

export const useRpiDrawerInputStyles = createStyles<RpiDrawerInputStyles, UseRpiDrawerInputStylesProps>(
  (theme, { isError, isDisabled, isFocused, isEmpty }) => {
    return {
      root: {
        borderRadius: 6,
        background: theme.colors.sky[5],
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: isError ? theme.colors.spark[6] : isFocused ? theme.colors.brand[5] : theme.colors.calm[5],
        position: "relative",
        cursor: isDisabled ? "not-allowed !important" : "text",
        height: 64,

        ...(isFocused === undefined && {
          "&:focus-within": {
            borderColor: isError ? theme.colors.spark[6] : theme.colors.brand[5],
          },
        }),
      },
      label: {
        ...theme.other.fontTypes.p3,
        fontWeight: theme.other.fontWeights.light,
        color: isDisabled ? theme.colors.brand[3] : theme.colors.brand[5],

        position: "absolute",
        pointerEvents: "none",

        left: 8,

        ...(isEmpty === undefined
          ? { top: 8, transform: "translateY(0)" }
          : {
              top: isEmpty && !isFocused ? "50%" : 8,
              transform: isEmpty && !isFocused ? "translateY(-50%)" : "translateY(0)",
            }),
      },
      input: {
        ...theme.other.fontTypes.p2,
        fontWeight: theme.other.fontWeights.light,
        lineHeight: "16px",
        color: isDisabled ? theme.colors.text[3] : theme.colors.text[5],
        outline: "none",
        background: "transparent",
        border: "none",
        padding: 8,
        margin: 0,
        width: "100%",
        cursor: isDisabled ? "not-allowed !important" : undefined,

        ...(isEmpty === undefined && {
          "&:focus ~ label": {
            top: 8,
            transform: "translateY(0)",
          },

          "&:placeholder-shown ~ *": {
            top: "50%",
            transform: "translateY(-50%)",
          },
        }),

        "&:-webkit-autofill, &:-webkit-autofill:focus": {
          borderRadius: 6,
        },
        "&::placeholder": {
          color: "transparent",
          userSelect: "none",
        },
      },
      error: {
        ...theme.other.fontTypes.p3,
        fontWeight: theme.other.fontWeights.light,
        color: theme.colors.spark[6],
      },
    };
  }
);

export interface RpiDrawerInputProps {
  label?: string;
  error?: string;
  fullWidth?: boolean;
  classNames?: Partial<Record<RpiDrawerInputStyles, string>>;
  styles?:
    | Partial<Record<RpiDrawerInputStyles, CSSObject>>
    | ((theme: MantineTheme) => Partial<Record<RpiDrawerInputStyles, CSSObject>>);
}

interface RpiDrawerLabelProps {
  label?: RpiDrawerInputProps["label"];
  required?: boolean;
}

export const RpiDrawerLabel: React.FC<RpiDrawerLabelProps> = ({ label, required }) => {
  return (
    <>
      {label}
      {required && <Box component="sup" sx={(theme) => ({ color: theme.colors.spark[5] })} children="*" />}
    </>
  );
};

type RpiDrawerTextInputProps = RpiDrawerInputProps &
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

export const RpiDrawerTextInput = React.forwardRef<HTMLInputElement, RpiDrawerTextInputProps>(
  (
    {
      label,
      error,
      fullWidth,
      classNames,
      styles: initialStyles,

      ...inputProps
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const { classes, cx } = useRpiDrawerInputStyles({ isError: !!error, isDisabled: inputProps.disabled });

    const styles = React.useMemo(() => {
      if (!initialStyles) return {};
      return typeof initialStyles === "function" ? initialStyles(theme) : initialStyles;
    }, [initialStyles]);

    const inputRef = React.useRef<HTMLInputElement>();
    const refs = useMergedRef(inputRef as React.MutableRefObject<HTMLInputElement>, ref);

    return (
      <Flex.Column gap={8} fullWidth={fullWidth}>
        <Box className={cx(classes.root, classNames?.root)} sx={styles.root} onClick={() => inputRef.current?.focus()}>
          <Flex.Column justify="flex-end" sx={{ height: "100%" }}>
            <Box
              placeholder={label || "placeholder"}
              {...inputProps}
              component="input"
              ref={refs as any}
              type="text"
              className={cx(classes.input, classNames?.input)}
              sx={styles.input}
            />

            {label && (
              <Box component="label" className={cx(classes.label, classNames?.label)} sx={styles.label}>
                <RpiDrawerLabel label={label} required={inputProps.required} />
              </Box>
            )}
          </Flex.Column>
        </Box>

        {error && <Box className={cx(classes.error, classNames?.error)} sx={styles.error} children={error} />}
      </Flex.Column>
    );
  }
);

type RpiDrawerNumberInputProps = RpiDrawerInputProps &
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

export const RpiDrawerNumberInput = React.forwardRef<HTMLInputElement, RpiDrawerNumberInputProps>(
  (
    {
      label,
      error,
      fullWidth,
      classNames,
      styles: initialStyles,

      onInput,
      ...inputProps
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const { classes, cx } = useRpiDrawerInputStyles({ isError: !!error, isDisabled: inputProps.disabled });

    const styles = React.useMemo(() => {
      if (!initialStyles) return {};
      return typeof initialStyles === "function" ? initialStyles(theme) : initialStyles;
    }, [initialStyles]);

    const inputRef = React.useRef<HTMLInputElement>();
    const refs = useMergedRef(inputRef as React.MutableRefObject<HTMLInputElement>, ref);

    return (
      <Flex.Column gap={8} fullWidth={fullWidth}>
        <Box className={cx(classes.root, classNames?.root)} sx={styles.root} onClick={() => inputRef.current?.focus()}>
          <Flex.Column justify="flex-end" sx={{ height: "100%" }}>
            <Box
              placeholder={label || "placeholder"}
              {...inputProps}
              component="input"
              ref={refs as any}
              type="number"
              className={cx(classes.input, classNames?.input)}
              sx={styles.input}
              onInput={(e: any) => {
                if ("max" in inputProps && inputProps.max !== undefined) {
                  const max = typeof inputProps.max === "string" ? Number.parseFloat(inputProps.max) : inputProps.max;
                  if (Number.parseFloat(e.target.value) > max) {
                    e.target.value = max;
                  }
                }

                if ("min" in inputProps && inputProps.min !== undefined) {
                  const min = typeof inputProps.min === "string" ? Number.parseFloat(inputProps.min) : inputProps.min;
                  if (Number.parseFloat(e.target.value) < min) {
                    e.target.value = min;
                  }
                }

                onInput && onInput(e);
              }}
            />

            {label && (
              <Box component="label" className={cx(classes.label, classNames?.label)} sx={styles.label}>
                <RpiDrawerLabel label={label} required={inputProps.required} />
              </Box>
            )}
          </Flex.Column>
        </Box>

        {error && <Box className={cx(classes.error, classNames?.error)} sx={styles.error} children={error} />}
      </Flex.Column>
    );
  }
);

type RpiDrawerPasswordInputProps = RpiDrawerInputProps &
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

export const RpiDrawerPasswordInput = React.forwardRef<HTMLInputElement, RpiDrawerPasswordInputProps>(
  (
    {
      label,
      error,
      fullWidth,
      classNames,
      styles: initialStyles,

      ...inputProps
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const { classes, cx } = useRpiDrawerInputStyles({ isError: !!error, isDisabled: inputProps.disabled });

    const [showPassword, setShowPassword] = React.useState<boolean>(false);
    const [warning, setWarning] = React.useState<string>();

    const styles = React.useMemo(() => {
      if (!initialStyles) return {};
      return typeof initialStyles === "function" ? initialStyles(theme) : initialStyles;
    }, [initialStyles]);

    const inputRef = React.useRef<HTMLInputElement>();
    const refs = useMergedRef(inputRef as React.MutableRefObject<HTMLInputElement>, ref);

    return (
      <Flex.Column gap={8} fullWidth={fullWidth}>
        <Box className={cx(classes.root, classNames?.root)} sx={styles.root}>
          <Flex.Row fullWidth sx={{ height: "100%" }}>
            <Flex.Column
              justify="flex-end"
              sx={{ flex: 1 }}
              onClick={() => inputRef.current?.focus()}
              onKeyDown={(e) => {
                setWarning(e.getModifierState("CapsLock") ? "Caps Lock is on" : undefined);
              }}
            >
              <Box
                placeholder={label || "placeholder"}
                {...inputProps}
                component="input"
                ref={refs as any}
                type={showPassword ? "text" : "password"}
                className={cx(classes.input, classNames?.input)}
                sx={styles.input}
              />

              {label && (
                <Box component="label" className={cx(classes.label, classNames?.label)} sx={styles.label}>
                  <RpiDrawerLabel label={label} required={inputProps.required} />
                </Box>
              )}
            </Flex.Column>

            <RpiIconButton
              icon={showPassword ? IconV2.Eye : IconV2.EyeOff}
              size={{ width: 18 }}
              color="currentColor"
              sx={(theme) => ({ color: theme.colors.brand[5], padding: 8 })}
              onClick={() => setShowPassword((showPassword) => !showPassword)}
            />
          </Flex.Row>
        </Box>

        {(error || warning) && (
          <Box
            className={cx(classes.error, classNames?.error)}
            sx={styles.error}
            children={
              error ? (
                error
              ) : (
                <Flex.Row align="center" gap={4}>
                  <IconV2.ErrorSolid size={14} color="currentColor" />
                  {warning}
                </Flex.Row>
              )
            }
          />
        )}
      </Flex.Column>
    );
  }
);

type RpiDrawerBraintreeInputProps = Omit<RpiDrawerInputProps, "error"> &
  Omit<PolymorphicComponentProps<"div", BoxProps>, "styles" | "className"> & {
    required?: boolean;
    onFocus?: () => void;
    isFocused?: boolean;
    isEmpty?: boolean;
    error?: string | boolean;
  };

export const RpiDrawerBraintreeInput = React.forwardRef<HTMLDivElement, RpiDrawerBraintreeInputProps>(
  (
    {
      label,
      error,
      fullWidth,
      classNames,
      styles: initialStyles,
      onFocus,
      isFocused,
      isEmpty,

      ...inputProps
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const { classes, cx } = useRpiDrawerInputStyles({ isError: !!error, isFocused: !!isFocused, isEmpty: !!isEmpty });

    const styles = React.useMemo(() => {
      if (!initialStyles) return {};
      return typeof initialStyles === "function" ? initialStyles(theme) : initialStyles;
    }, [initialStyles]);

    const inputRef = React.useRef<HTMLDivElement>();
    const refs = useMergedRef(inputRef as React.MutableRefObject<HTMLDivElement>, ref);

    return (
      <Flex.Column gap={8} fullWidth={fullWidth}>
        <Box className={cx(classes.root, classNames?.root)} sx={styles.root} onClick={() => onFocus && onFocus()}>
          <Flex.Column justify="flex-end" sx={{ height: "100%" }}>
            <Box
              placeholder={label || "placeholder"}
              {...inputProps}
              ref={refs as any}
              className={cx(classes.input, classNames?.input)}
              sx={[styles.input, { height: 34.39, padding: 0 }]}
            />

            {label && (
              <Box component="label" className={cx(classes.label, classNames?.label)} sx={styles.label}>
                <RpiDrawerLabel label={label} required={inputProps.required} />
              </Box>
            )}
          </Flex.Column>
        </Box>

        {typeof error === "string" && (
          <Box className={cx(classes.error, classNames?.error)} sx={styles.error} children={error} />
        )}
      </Flex.Column>
    );
  }
);

export * from "./drawers/RpiDrawerSelectInput";
